import tw, { styled } from 'twin.macro';
import {
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
  MapPinIcon,
  RocketLaunchIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';

import { ButtonV2, PresentationCard, Title } from '@restworld/ui-ds';
import { useMemo, useState } from 'react';
import { toDateFormat } from '@restworld/utils-common';
import { WorkerPastExperience } from '@restworld/utility-types';
import { animated, useSpring } from '@react-spring/web';

export const WorkerExperienceItem = ({
  workerPastExperience,
}: {
  experienceIndex: number;
  workerPastExperience: WorkerPastExperience;
}) => {
  const { t } = useTranslation('common');
  const errorMessage = t('missing_info');
  const [expandMore, setExpandMore] = useState(false);
  const { sector, role, restaurantName, startDate, endDate, description, city, skills, isActive } = useMemo(() => {
    const sector = workerPastExperience?.domain_interest_var?.label;
    const role = workerPastExperience?.occupation?.label_it.label;
    const restaurantName = workerPastExperience?.restaurant_name;
    const startDate = workerPastExperience?.from_date
      ? toDateFormat(workerPastExperience?.from_date, 'dd/MM/yyyy')
      : undefined;
    const endDate = workerPastExperience?.to_date
      ? toDateFormat(workerPastExperience?.to_date, 'dd/MM/yyyy')
      : undefined;
    const description = workerPastExperience?.description;
    const city = workerPastExperience?.city;
    const skills = (workerPastExperience?.skills?.map((skill) => skill?.label_it?.label) || []).filter(
      (skill) => skill
    );
    const isActive = workerPastExperience?.currently_working;
    return {
      sector,
      role,
      restaurantName,
      startDate,
      endDate,
      description,
      city,
      skills,
      isActive,
    };
  }, [
    workerPastExperience?.domain_interest_var?.label,
    workerPastExperience?.occupation?.label_it.label,
    workerPastExperience?.restaurant_name,
    workerPastExperience?.from_date,
    workerPastExperience?.to_date,
    workerPastExperience?.description,
    workerPastExperience?.city,
    workerPastExperience?.skills,
    workerPastExperience?.currently_working,
  ]);
  const expandMoreStyles = useSpring({
    opacity: expandMore ? 1 : 0,
    transform: expandMore ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const expandLessStyles = useSpring({
    opacity: !expandMore ? 1 : 0,
    transform: !expandMore ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const roleEl = (
    <PresentationCard
      label={t('common:role')}
      header={role || errorMessage}
      isError={!role}
      icon={<UserIcon tw="w-6 h-6" />}
    />
  );

  const restaurantNameEl = (
    <PresentationCard
      label={t('common:local')}
      header={restaurantName || errorMessage}
      icon={<BuildingOfficeIcon tw="w-6 h-6" />}
      isError={!restaurantName}
    />
  );

  const startDateEl = (
    <PresentationCard
      label={t('common:start_date')}
      header={startDate || errorMessage}
      isError={!startDate}
      icon={<RocketLaunchIcon tw="w-6 h-6" />}
    />
  );

  const endDateEl = (
    <PresentationCard
      label={t('common:end_date')}
      header={endDate || (!isActive ? errorMessage : t('common:present'))}
      icon={<FlagIcon tw="w-6 h-6" />}
      isError={!endDate && !isActive}
    />
  );

  return (
    <div tw="flex flex-col w-full overflow-hidden">
      {!expandMore && (
        <CardsContainer style={expandLessStyles}>
          <CardsCol>
            {roleEl}
            {restaurantNameEl}
          </CardsCol>
          <CardsCol>
            {startDateEl}
            {endDateEl}
          </CardsCol>
        </CardsContainer>
      )}
      {expandMore && (
        <CardsContainer style={expandMoreStyles}>
          <CardsCol>
            <PresentationCard
              label={t('common:sector')}
              header={sector || errorMessage}
              icon={<BriefcaseIcon tw="w-6 h-6" />}
              isError={!sector}
            />
            {roleEl}
            {restaurantNameEl}
          </CardsCol>
          <CardsCol>
            {startDateEl}
            {endDateEl}
            <PresentationCard
              label={t('common:work_location')}
              header={city || errorMessage}
              hide={!expandMore}
              icon={<MapPinIcon tw="w-6 h-6" />}
              isError={!city}
            />
          </CardsCol>
        </CardsContainer>
      )}
      {expandMore && (
        <animated.div style={expandMoreStyles} tw="flex flex-col w-full items-start mt-4 gap-4">
          <PresentationCard
            label={t('common:job_description')}
            header={description || errorMessage}
            icon={<BookOpenIcon tw="w-6 h-6" />}
            isError={!description}
          />
        </animated.div>
      )}
      {expandMore && (
        <animated.div style={expandMoreStyles} tw="flex flex-col gap-4 items-start w-full mt-6">
          <Title variant="h6-large" fontWeight="medium" textColor="black">
            {t('common:skills_learned')}
          </Title>
          <div tw="grid lg:grid-cols-2 grid-cols-1 gap-x-9 gap-y-4 w-full">
            {skills.map((skill, index) => (
              <div key={index} tw="flex flex-row gap-2 items-center justify-start">
                <CheckCircleIcon tw="w-6 h-6 min-w-[24px] text-primary-700" />
                <Title variant="h6-large" fontWeight="normal" textColor="gray-600">
                  {skill}
                </Title>
              </div>
            ))}
            {skills.length === 0 && errorMessage}
          </div>
        </animated.div>
      )}
      <div tw="flex flex-row w-full justify-center items-start mt-6">
        <ButtonV2
          StartIcon={!expandMore ? ChevronDownIcon : ChevronUpIcon}
          variant="secondary-restworld"
          size="sm"
          twStyle={tw`focus:ring-0 p-0`}
          onClick={() => setExpandMore((prev) => !prev)}
        >
          {!expandMore ? t('common:show_more_information') : t('common:show_less_information')}
        </ButtonV2>
      </div>
    </div>
  );
};

export const CardsContainer = styled(animated.div)`
  ${tw`flex lg:flex-row flex-col lg:gap-8 gap-4 w-full`}
`;

export const CardsCol = styled.div`
  ${tw`flex flex-col lg:w-1/2 w-full items-start justify-start gap-4`}
`;
